import React from "react";
import {
  FileText,
  ClipboardList,
  CheckCircle,
  DollarSign,
  Users,
  AlertCircle,
  UserCog,
} from "lucide-react";

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number | string;
  color: 'blue' | 'gray' | 'purple' | 'green' | 'orange' | 'indigo' | 'yellow' | 'cyan' | 'emerald' | 'rose' | 'teal';
}

const StatCard = ({ icon, title, value, color }: StatCardProps) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    gray: "bg-gray-50 text-gray-700 border-gray-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    green: "bg-green-50 text-green-700 border-green-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  };

  return (
    <div
      className={`${colorClasses[color]} rounded-lg border p-2.5 transition-all hover:shadow-md hover:scale-105`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium opacity-75 mb-0.5">{title}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
        <div className="opacity-60">{icon}</div>
      </div>
    </div>
  );
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  color: string;
}

const SectionHeader = ({ icon, title, color }: SectionHeaderProps) => (
  <div
    className={`flex items-center gap-2 mb-2.5 pb-1.5 border-b border-${color}-200`}
  >
    <div className={`text-${color}-600`}>{icon}</div>
    <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
  </div>
);

export interface StatsData {
  totalApplications?: number;
  applicationStatus?: {
    DRAFT?: number;
    SUBMITTED?: number;
    ACCEPTED?: number;
    REJECTED?: number;
  };
  paymentStatus?: {
    PAID?: number;
    VERIFIED?: number;
    PENDING?: number;
  };
  genders?: {
    MALE?: number;
    M?: number;
    FEMALE?: number;
    F?: number;
  };
  pwd?: {
    yes?: number;
  };
  governmentServant?: {
    yes?: number;
  };
  candidateTypes?: {
    MEDICAL?: number;
    "NON-MEDICAL"?: number;
  };
  categoryOfCandidate?: Record<string, number>;
}
interface DashboardStatsProps {
  stats: StatsData;
}

export default function DashboardStats({ stats }: DashboardStatsProps) {
  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Applications */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<FileText className="w-4 h-4" />}
              title="Applications Overview"
              color="blue"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <StatCard
                icon={<FileText size={18} />}
                title="Total"
                value={stats?.totalApplications || 0}
                color="blue"
              />
              <StatCard
                icon={<ClipboardList size={18} />}
                title="Draft"
                value={stats?.applicationStatus?.DRAFT || 0}
                color="gray"
              />
              <StatCard
                icon={<ClipboardList size={18} />}
                title="Submitted"
                value={stats?.applicationStatus?.SUBMITTED || 0}
                color="blue"
              />
              <StatCard
                icon={<CheckCircle size={18} />}
                title="Accepted"
                value={stats?.applicationStatus?.ACCEPTED || 0}
                color="green"
              />
              <StatCard
                icon={<AlertCircle size={18} />}
                title="Rejected"
                value={stats?.applicationStatus?.REJECTED || 0}
                color="rose"
              />
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<DollarSign className="w-4 h-4" />}
              title="Payment Summary"
              color="green"
            />
            <div className="grid grid-cols-3 gap-2.5">
              <StatCard
                icon={<DollarSign size={18} />}
                title="Paid"
                value={stats?.paymentStatus?.PAID || 0}
                color="green"
              />
              <StatCard
                icon={<CheckCircle size={18} />}
                title="Verified"
                value={stats?.paymentStatus?.VERIFIED || 0}
                color="emerald"
              />
              <StatCard
                icon={<DollarSign size={18} />}
                title="Pending"
                value={stats?.paymentStatus?.PENDING || 0}
                color="orange"
              />
            </div>
          </div>

          {/* Candidate Details */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<Users className="w-4 h-4" />}
              title="Candidate Details"
              color="indigo"
            />
            <div className="grid grid-cols-3 gap-2.5">
              <StatCard
                icon={<Users size={18} />}
                title="Male"
                value={stats?.genders?.MALE || stats?.genders?.M || 0}
                color="indigo"
              />
              <StatCard
                icon={<Users size={18} />}
                title="Female"
                value={stats?.genders?.FEMALE || stats?.genders?.F || 0}
                color="rose"
              />
              <StatCard
                icon={<AlertCircle size={18} />}
                title="PwD"
                value={stats?.pwd?.yes || 0}
                color="yellow"
              />
              <StatCard
                icon={<UserCog size={18} />}
                title="Govt. Servant"
                value={stats?.governmentServant?.yes || 0}
                color="cyan"
              />
              <StatCard
                icon={<Users size={18} />}
                title="Medical"
                value={stats?.candidateTypes?.MEDICAL || 0}
                color="emerald"
              />
              <StatCard
                icon={<Users size={18} />}
                title="Non-Medical"
                value={stats?.candidateTypes?.["NON-MEDICAL"] || 0}
                color="rose"
              />
            </div>
          </div>

          {/* Category */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<ClipboardList className="w-4 h-4" />}
              title="Category Breakdown"
              color="teal"
            />
            <div className="grid grid-cols-2 gap-2.5">
              {Object.entries(stats?.categoryOfCandidate || {}).map(([key, value]) => (
                <StatCard
                  key={key}
                  icon={<Users size={18} />}
                  title={key}
                  value={value as any}
                  color="teal"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
